import { formatIndian } from './format';

/** Money is held as whole paise so sums never drift. */
export const toPaise = (rupees: number) => Math.round(rupees * 100);
export const toRupees = (paise: number) => paise / 100;

/** "₹1,23,456.50", or "₹350" when there are no paise. */
export function formatRupees(paise: number): string {
  const r = Math.abs(paise) / 100;
  return `${paise < 0 ? '−' : ''}₹${formatIndian(r, Number.isInteger(r) ? 0 : 2)}`;
}

export const PAYMENT_METHODS = ['cash', 'upi', 'card', 'bank', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  bank: 'Bank transfer',
  other: 'Other',
};

/** The tests of a report and what each cost when it was chosen. Kept with the report, away from its content. */
export interface PriceItem {
  templateId?: string;
  name: string;
  pricePaise: number;
}

export function priceItemsFor(
  docs: readonly { name: string; price?: number | undefined; templateId?: string | undefined }[],
): PriceItem[] {
  return docs.map((d) => ({
    ...(d.templateId ? { templateId: d.templateId } : {}),
    name: d.name,
    pricePaise: toPaise(d.price ?? 0),
  }));
}

export type PaymentState = 'free' | 'unpaid' | 'part' | 'paid' | 'over';

export interface Billing {
  /** Sum of the test prices when the report was made. */
  pricePaise: number;
  discountPaise: number;
  /** What the patient should pay: price less discount, never below zero. */
  duePaise: number;
  paidPaise: number;
  /** Still to pay; zero when paid in full or more. */
  balancePaise: number;
  /** Paid beyond what was due. */
  excessPaise: number;
  state: PaymentState;
}

export function billingOf(pricePaise: number, discountPaise: number, paidPaise: number): Billing {
  const due = Math.max(0, pricePaise - Math.max(0, discountPaise));
  const balance = Math.max(0, due - paidPaise);
  const state: PaymentState =
    due === 0 && paidPaise === 0
      ? 'free'
      : paidPaise > due
        ? 'over'
        : paidPaise === due
          ? 'paid'
          : paidPaise > 0
            ? 'part'
            : 'unpaid';
  return {
    pricePaise,
    discountPaise,
    duePaise: due,
    paidPaise,
    balancePaise: balance,
    excessPaise: Math.max(0, paidPaise - due),
    state,
  };
}

export const PAYMENT_STATE_LABELS: Record<PaymentState, string> = {
  free: 'No charge',
  unpaid: 'Unpaid',
  part: 'Part paid',
  paid: 'Paid',
  over: 'Paid extra',
};
